import { Button } from '@common/components';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import Camera from '@/assets/svgs/permission/camera.svg';
import ImgPicker from '@/assets/svgs/permission/img-picker.svg';
import Location from '@/assets/svgs/permission/location.svg';
import Notification from '@/assets/svgs/permission/notification.svg';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import type { DeviceTokenData } from '@/stores/DeviceStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';

import { MESSAGE_KEYWORD, useHandleApp } from '..';

export const AppModal = () => {
  const [appModal, setAppModal, setTempDeviceTokenData] = useDeviceInfoStore(
    useShallow((state) => [
      state.appModal,
      state.setAppModal,
      state.setTempDeviceTokenData,
    ]),
  );

  const { isOpened, type } = appModal;

  const { request: requestExit } = useHandleApp({
    type: MESSAGE_KEYWORD.EXIT_APP,
  });

  const { request: requestRestart } = useHandleApp({
    type: MESSAGE_KEYWORD.RESTART_APP,
  });

  const closeModal = () =>
    setAppModal({
      type,
      isOpened: false,
    });

  const { request: requestCheckPermission } = useHandleApp({
    type: MESSAGE_KEYWORD.CHECK_PERMISSION,
  });

  const { request: requestNotification, response: notificationResponse } =
    useHandleApp<DeviceTokenData>({
      type: MESSAGE_KEYWORD.NOTIFICATION,
    });

  const [isSwiftPending, setIsSwiftPending] = useState(false);

  useEffect(() => {
    if (!isSwiftPending) return;

    if (
      navigator.userAgent.includes('swift') ||
      navigator.userAgent.includes('ios')
    ) {
      setTimeout(requestNotification, 5_000);
    }
  }, [isSwiftPending, requestNotification]);

  useEffect(() => {
    const { data, isSuccess } = notificationResponse ?? {};

    if (isSuccess && data) {
      if (
        navigator.userAgent.includes('swift') ||
        navigator.userAgent.includes('ios')
      ) {
        if (data.fcm_token) {
          setTempDeviceTokenData(data);
          setIsSwiftPending(false);
        } else {
          setIsSwiftPending(true);
        }
      } else {
        setTempDeviceTokenData(data);
      }

      requestCheckPermission({
        permissionType: MESSAGE_KEYWORD.LOCATION,
      });
    }
  }, [notificationResponse, requestCheckPermission, setTempDeviceTokenData]);

  return (
    <ResponsiveDialog
      opened={isOpened}
      onClose={() => {
        //! ⚠️ 권한 신청 창은 확인 버튼 클릭 시에만 닫힘
        if (type !== 'permission') {
          closeModal();
        }
      }}
      drawerType="bottom"
    >
        {
          {
            permission: (
              <article className="flex h-full max-h-[70vh] flex-col">
                <h2 className="mb-8 font-700 typo-body1">
                  서비스 이용을 위한
                  <br />
                  권한 접근이 필요해요.
                </h2>
                <div className="flex flex-1 flex-col gap-20 overflow-y-auto">
                  <section className="flex flex-col gap-16 border-b-1 border-Gray-300 pb-20">
                    <div className="flex flex-col gap-2">
                      <h3 className="font-600 text-Gray-900 typo-body4">
                        필수 접근 권한
                      </h3>
                      <p className="text-Gray-600 typo-body4">
                        필수적 접근 권한 없음
                      </p>
                    </div>
                  </section>
                  <section className="flex flex-col gap-16 border-b-1 border-Gray-300 pb-20">
                    <div className="flex flex-col gap-2">
                      <h3 className="font-600 text-Gray-900 typo-body4">
                        선택적 접근 권한
                      </h3>
                      <p className="text-Gray-600 typo-body4">
                        선택적 접근 권한은 해당 기능을 사용할 때 허용이
                        필요하며, 비허용 시에도 해당 기능 외 서비스 이용이
                        가능합니다.
                      </p>
                    </div>
                    <ul className="flex flex-col gap-10 px-16">
                      {[
                        {
                          icon: Notification,
                          title: '알림(선택)',
                          description: '개인별 맞춤 정보 알림',
                        },
                        {
                          icon: Location,
                          title: '위치(선택)',
                          description: '검색 및 동네소식에서 현재 위치 사용',
                        },
                        {
                          icon: Camera,
                          title: '카메라(선택)',
                          description: '답례품 후기, 문의, 프로필 이미지 등록',
                        },
                        {
                          icon: ImgPicker,
                          title: '사진(선택)',
                          description: '답례품 후기, 문의, 프로필 이미지 등록',
                        },
                      ].map(({ icon: Icon, title, description }, index) => (
                        <li key={index} className="flex items-center gap-16">
                          <div className="size-48 rounded-pill bg-Gray-100 flex-center">
                            <Icon alt={title} />
                          </div>
                          <div className="flex flex-col gap-2">
                            <h4 className="font-700 text-Gray-900 typo-body4">
                              {title}
                            </h4>
                            <p className="text-Gray-600 typo-body4">
                              {description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                  <section className="flex flex-col gap-16 pb-16">
                    <div className="flex flex-col gap-2">
                      <h3 className="font-600 text-Gray-900 typo-body4">
                        접근 권한 변경 방법
                      </h3>
                      <p className="text-Gray-600 typo-body4">
                        휴대폰 설정 {`>`} 앱 또는 애플리케이션 관리 {`>`} 웰로{' '}
                        {`>`} 권한에서 각 권한별 변경 가능합니다.
                      </p>
                    </div>
                  </section>
                </div>
                <Button
                  className="mt-16"
                  onClick={async () => {
                    requestNotification();

                    closeModal();
                  }}
                >
                  확인
                </Button>
              </article>
            ),
            exit: (
              <article className="flex flex-col items-center gap-8">
                <h2 className="text-center font-700 typo-h5">
                  앱을 나가시겠어요?
                </h2>
                <p className="mb-16 text-Gray-600 typo-body4">
                  더이상 뒤로 갈 페이지가 없어요.
                </p>
                <Button onClick={closeModal}>계속 볼게요</Button>
                <Button theme="sub-text" onClick={() => requestExit()}>
                  나갈게요
                </Button>
              </article>
            ),
            restart: (
              <article className="flex flex-col items-center gap-8">
                <h2 className="text-center font-700 typo-h5">
                  앱이 업데이트 되었어요!
                </h2>
                <p className="mb-16 text-Gray-600 typo-body4">
                  사용자들의 의견을 반영한 업데이트가 있어요.
                  <br />
                  업데이트 하고 신규 기능을 이용해 보세요.
                </p>
                <Button onClick={() => requestRestart()}>업데이트</Button>
                <Button theme="sub-text" onClick={closeModal}>
                  다음에 할래요
                </Button>
              </article>
            ),
          }[type]
        }
    </ResponsiveDialog>
  );
};
